/**
 * A group of related fields, with the group's name as its legend.
 *
 * Screens grouped fields with a heading and a border, which says nothing to a
 * screen reader: it reads each control's own label with no sense of which
 * group it belongs to. A real `fieldset` and `legend` carry that.
 *
 * @module components/ui/Form/FieldSet
 */

import React, { type ReactNode } from "react";

export interface FieldSetProps {
  /** Names the group. Sentence case, a few words. */
  legend: ReactNode;
  /** What the group is for, when the legend alone is not enough. */
  description?: ReactNode;
  /** One or two columns; two only where the fields are genuinely paired. */
  columns?: 1 | 2;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

const COLUMN_CLASS: Record<1 | 2, string> = {
  1: "",
  2: "sm:grid-cols-2",
};

/**
 * @example
 * ```tsx
 * <FieldSet legend="Programme details" columns={2}>
 *   <FormField label="Title" htmlFor="title"><TextField id="title" … /></FormField>
 * </FieldSet>
 * ```
 */
export function FieldSet({
  legend,
  description = undefined,
  columns = 1,
  children,
  className = "",
  "data-testid": testId,
}: FieldSetProps): React.ReactElement {
  return (
    <fieldset className={`min-w-0 ${className}`.trim()} data-testid={testId}>
      {/* Not daisyUI's .fieldset: that makes the element a grid, and a legend
          is not a grid item. The grid goes on the fields below it instead. */}
      <legend className="fieldset-legend p-0 text-md">{legend}</legend>
      {description && <p className="mt-1 text-md text-muted">{description}</p>}
      <div className={`mt-4 grid gap-5 ${COLUMN_CLASS[columns]}`.trim()}>{children}</div>
    </fieldset>
  );
}
